import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import SEOHead from "@/components/base/SEOHead";

const aboutSchema = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "关于爱威奶",
  "description": "了解爱威奶品牌故事、发展历程、品牌口碑及联系方式。",
  "url": "https://luojunji1.cn/about",
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<"idle" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formEl = e.currentTarget;
    const formData = new FormData(formEl);

    const honeypotVal = (formData.get("phone_alt") as string || "").trim();
    if (honeypotVal) {
      setFormStatus("success");
      formEl.reset();
      setTimeout(() => setFormStatus("idle"), 3000);
      return;
    }

    formData.delete("phone_alt");

    try {
      const submitAddr = (formEl.querySelector("input[name='submitAddr']") as HTMLInputElement)?.value;
      if (!submitAddr) {
        setFormStatus("error");
        setFormError("表单地址未配置");
        return;
      }

      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        params.append(key, value as string);
      });

      const response = await fetch(submitAddr, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === "OK") {
        setFormStatus("success");
        formEl.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
        setFormStatus("error");
        setFormError(serverMsg.includes("spam") || serverMsg.includes("form data is spam") ? "提交失败，请稍后重试" : (serverMsg || "提交失败，请稍后重试"));
      }
    } catch {
      setFormStatus("error");
      setFormError("网络错误，请检查网络连接后重试");
    }

    setTimeout(() => {
      if (formStatus !== "idle") setFormStatus("idle");
    }, 5000);
  };

  return (
    <>
      <SEOHead
        title="关于爱威奶 - 品牌故事、口碑与联系方式"
        description="了解爱威奶品牌故事、品牌发展历程、用户口碑及联系方式。爱威奶致力于为每一个家庭提供高品质营养产品。"
        keywords="爱威奶,关于爱威奶,爱威奶品牌,爱威奶口碑,爱威奶联系方式"
        canonicalUrl="https://luojunji1.cn/about"
        ogTitle="关于爱威奶 - 品牌故事、口碑与联系方式"
        ogDescription="了解爱威奶品牌故事、品牌发展历程、用户口碑及联系方式。"
        ogUrl="https://luojunji1.cn/about"
        schemaJson={aboutSchema}
      />

      <Navbar />

      <main className="pt-16 md:pt-18">
        <div className="bg-background-100 py-10 md:py-14">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-4">
              <Link to="/" className="hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer">首页</Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-800 font-medium">关于我们</span>
            </nav>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900">
              关于爱威奶
            </h1>
            <p className="mt-2 text-sm text-foreground-600">呵护每一刻，品质新选择</p>
          </div>
        </div>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14 items-center">
              <div>
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-5">品牌故事</h2>
                <div className="space-y-4 text-sm md:text-base text-foreground-700 leading-relaxed">
                  <p>
                    爱威奶品牌创立于2015年，源于创始团队对健康营养事业的热爱与执着。我们深知，优质的营养是一家人健康生活的基石，因此从创立之初就确立了&ldquo;品质为先、用户为本&rdquo;的品牌理念。
                  </p>
                  <p>
                    经过多年的研发投入和市场验证，爱威奶已发展成为覆盖儿童、成人、中老年及运动营养等多条产品线的综合性营养品牌。我们的产品采用科学配方和天然原料，经过严格品控流程，确保每一罐产品都达到最高品质标准。
                  </p>
                  <p>
                    目前爱威奶已在全国200多个城市建立销售网络，累计服务超过100万家庭，用户满意度高达98.6%。我们不仅是一家营养品企业，更是万千家庭健康生活的忠实伙伴。
                  </p>
                </div>
              </div>
              <div className="w-full h-72 md:h-80 rounded-lg overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Warm%20minimalist%20interior%20photography%20of%20a%20modern%20clean%20office%20space%20with%20cream%20colored%20walls%20and%20natural%20wood%20furniture%20soft%20sunlight%20streaming%20through%20large%20windows%20green%20plants%20decorating%20the%20space%20warm%20inviting%20atmosphere%20professional%20yet%20cozy%20brand%20story%20scene&width=1000&height=700&seq=about-brand-story&orientation=landscape"
                  alt="爱威奶品牌故事"
                  title="爱威奶品牌故事 - 品质为先，用户为本"
                  className="w-full h-full object-cover rounded-lg"
                />
              </div>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-100">
          <div className="w-full max-w-6xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">品牌口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
              {[
                { stat: "100万+", label: "服务家庭" },
                { stat: "98.6%", label: "用户满意度" },
                { stat: "200+", label: "覆盖城市" },
                { stat: "10年", label: "品质坚守" },
              ].map((item, i) => (
                <div key={i} className="bg-background-50 rounded-lg p-6 text-center cursor-default">
                  <p className={`font-heading text-3xl md:text-4xl font-bold ${i % 2 === 0 ? "text-primary-600" : "text-accent-600"}`}>
                    {item.stat}
                  </p>
                  <p className="text-sm text-foreground-500 mt-2">{item.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-18 bg-background-50">
          <div className="w-full max-w-4xl mx-auto px-4 md:px-6">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">联系我们</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-5">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                    <i className="ri-phone-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">客服热线</p>
                    <p className="text-sm text-foreground-600">400-888-9999</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-accent-50 text-accent-600 flex-shrink-0">
                    <i className="ri-mail-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">电子邮箱</p>
                    <p className="text-sm text-foreground-600">service@aiweinai.com</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-secondary-100 text-secondary-600 flex-shrink-0">
                    <i className="ri-map-pin-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">公司地址</p>
                    <p className="text-sm text-foreground-600">上海市浦东新区张江高科技园区爱威奶大厦</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-50 text-primary-600 flex-shrink-0">
                    <i className="ri-time-line text-lg"></i>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground-900">服务时间</p>
                    <p className="text-sm text-foreground-600">周一至周日 9:00 - 21:00</p>
                  </div>
                </div>
              </div>

              <div className="bg-background-100 rounded-lg p-6">
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-4">发送消息</h3>

                {formStatus === "success" && (
                  <div className="mb-4 p-3 bg-accent-50 text-accent-700 rounded-lg text-sm">
                    消息已发送成功！我们会尽快与您联系。
                  </div>
                )}
                {formStatus === "error" && (
                  <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-lg text-sm">
                    {formError}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-4" data-readdy-form="">
                  <input type="hidden" name="submitAddr" value="https://readdy.ai/api/form/d9q15a7nahb7382ov4h0" />
                  <div>
                    <input
                      type="text"
                      name="name"
                      placeholder="您的姓名"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <input
                      type="email"
                      name="email"
                      placeholder="电子邮箱"
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <textarea
                      name="message"
                      placeholder="请输入您的问题或建议（500字以内）"
                      rows={4}
                      maxLength={500}
                      required
                      className="w-full px-4 py-2.5 text-sm bg-background-50 border border-background-200 rounded-lg focus:outline-none focus:border-primary-300 transition-colors resize-none"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1 text-right">最多500字</p>
                  </div>
                  <div className="hp-field-wrap">
                    <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                  </div>
                  <button
                    type="submit"
                    className="w-full py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors duration-200 cursor-pointer whitespace-nowrap"
                  >
                    发送消息
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}